// ============================================================
// UNAWARE - a target that does not know it is being attacked.
//
// The attack is Easy (+30), and the target gets no Dodge and no Parry:
// you cannot react to what you have not noticed. It is a condition code
// (`Unw`) like any other, so the GM can hang it on anybody in any mode;
// Stealth adds its own answer (who the ladder says has not noticed), and
// both arrive here as one question.
//
// It ends when the target is attacked (hit or miss, they know now), or at
// the start of their own turn.
//
// The +30 is from the Deathwatch core rules as recalled, not checked
// against the book - see dev-docs/decisions-log.md, 2026-10-05.
//
// Pure. Zero React dependency.
// ============================================================
import { condType } from './conditions';

const UNAWARE_CODE = 'Unw';
const UNAWARE_TO_HIT = 30;

/** The ledger's line for it: a derived custom modifier, so the sum, the chip and the breakdown all show it. */
export const UNAWARE_MOD = Object.freeze({
  id: 'target-unaware',
  label: 'Target unaware (Easy)',
  value: UNAWARE_TO_HIT,
  derived: true,
});

const hasCode = (conditions: readonly string[] | null | undefined) =>
  !!conditions?.some((c) => condType(c) === UNAWARE_CODE);

/**
 * Is this target unaware? By its own condition, or because the caller's
 * view of the scene says so (`alsoUnaware`: Stealth's ladder).
 */
export function isUnaware(
  target: { conditions?: readonly string[] | null } | null | undefined,
  alsoUnaware = false,
): boolean {
  return !!target && (alsoUnaware || hasCode(target.conditions));
}

/** The conditions with Unaware on them; the same list when it already is. */
export function madeUnaware(conditions: readonly string[] | null | undefined): string[] {
  const conds = [...(conditions ?? [])];
  return hasCode(conds) ? conds : [...conds, UNAWARE_CODE];
}

/** The conditions without Unaware, and whether it was there: they have been attacked, and know it. */
export function alerted(conditions: readonly string[] | null | undefined): { conds: string[]; was: boolean } {
  const conds = (conditions ?? []).filter((c) => condType(c) !== UNAWARE_CODE);
  return { conds, was: conds.length !== (conditions ?? []).length };
}
